import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAppText } from 'src/i18n';
import IMFilesObject from 'src/interfaces/IMFilesObject';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface ObjectiveDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    isDirty: boolean;
    businessCategory_Id: number;
    referenceYear_Id: number;
    objectiveConfigs: ObjectiveConfigDto[];
    jobLevels: IMFilesObject[];
}

export enum ObjectiveConfigType {
    HrLevel = 1,
    Manager = 2,
}

export interface ObjectiveConfigDto {
    [key: string]: any;
    id: number;
    weight: number;
    isDirty: boolean;
    baseConfigFamily_Id: number;
    managerType_Id: number;
    objective_Id: number;
    noObjective: boolean;
    configType: ObjectiveConfigType;
    hasTeamLeadConfig: boolean;
    nameFr: string;
    nameEn: string;
    objectiveConfigCriterias: ObjectiveConfigCriteriaDto[];
    teamLeadObjectiveConfigCriterias: ObjectiveConfigCriteriaDto[];
}

const blankObjectiveConfigCriteria: ObjectiveConfigCriteriaDto = {
    id: 0,
    value: 0,
    objectiveCompletionPercentage: 0,
    objectiveConfig_Id: 0,
    objectiveConfigCriteriaType_Id: 0,
    isTeamLeadConfig: false,
    isDirty: false,
};

const blankTeamLeadObjectiveConfigCriteria: ObjectiveConfigCriteriaDto = {
    id: 0,
    value: 0,
    objectiveCompletionPercentage: 0,
    objectiveConfig_Id: 0,
    objectiveConfigCriteriaType_Id: 0,
    isTeamLeadConfig: true,
    managerTypeGroupSize_Id: 0,
    isDirty: false,
};

export interface ObjectiveConfigCriteriaDto {
    [key: string]: any;
    id: number;
    value: number;
    isDirty: boolean;
    isTeamLeadConfig: boolean;
    objectiveCompletionPercentage: number;
    objectiveConfig_Id: number;
    objectiveConfigCriteriaType_Id: number;
    managerTypeGroupSize_Id?: number;
}

export interface ObjectiveState {
    objectives: ObjectiveDto[];
    objective: ObjectiveDto;
    gettingObjectives: boolean;
    gettingObjective: boolean;
    archivingObjective: boolean;
    archivingConfig: boolean;
    archivingConfigCriteria: boolean;
    savingConfig: boolean;
    savingConfigCriteria: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
    savingConfigId: number;
}

const blankObjectiveConfigDto: ObjectiveConfigDto = {
    id: 0,
    weight: 0,
    objective_Id: 0,
    baseConfigFamily_Id: 0,
    managerType_Id: 0,
    isDirty: false,
    nameEn: '',
    nameFr: '',
    noObjective: false,
    hasTeamLeadConfig: false,
    objectiveConfigCriterias: [],
    teamLeadObjectiveConfigCriterias: [],
    configType: ObjectiveConfigType.HrLevel,
};

const blankObjectiveDto: ObjectiveDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    businessCategory_Id: 0,
    referenceYear_Id: 0,
    isDirty: false,
    objectiveConfigs: [blankObjectiveConfigDto],
    jobLevels: [],
};

const initialState: ObjectiveState = {
    objectives: [],
    objective: { ...blankObjectiveDto },
    gettingObjectives: false,
    gettingObjective: false,
    archivingObjective: false,
    archivingConfig: false,
    archivingConfigCriteria: false,
    savingConfig: false,
    savingConfigCriteria: false,
    status: 'idle',
    error: undefined,
    savingConfigId: 0,
};

export const objectiveSlice = createSlice({
    name: 'objectives',
    initialState,
    reducers: {
        clearObjectives: (state, param) => {
            state.objectives = [];
            state.objective = { ...blankObjectiveDto };
            state.objective.referenceYear_Id = param.payload;
        },
        updateObjective: (state, param) => {
            const { payload } = param;
            state.objective = payload;
        },
        updateObjectiveConfig: (state, param) => {
            const { payload } = param;
            const objectiveConfigToChange = state.objective.objectiveConfigs.find((x) => x.id === payload.id);

            if (objectiveConfigToChange) {
                objectiveConfigToChange.isDirty = true;
                objectiveConfigToChange[payload.fieldName as keyof ObjectiveDto] = payload.value;
            }
        },
        updateObjectiveConfigCriteria: (state, param) => {
            const { payload } = param;

            const objectiveConfig = state.objective.objectiveConfigs.find((x) => x.id === payload.objectiveConfigId);

            if (payload.isTeamLead) {
                const objectiveConfigCriteriaToChange = objectiveConfig?.teamLeadObjectiveConfigCriterias.find((x) => x.id === payload.id);

                if (objectiveConfigCriteriaToChange) {
                    objectiveConfigCriteriaToChange.isDirty = true;
                    objectiveConfigCriteriaToChange.isTeamLeadConfig = true;
                    objectiveConfigCriteriaToChange.objectiveConfig_Id = payload.objectiveConfigId;
                    objectiveConfigCriteriaToChange.objectiveConfigCriteriaType_Id = payload.currentCriteriaTypeId;
                    objectiveConfigCriteriaToChange[payload.fieldName as keyof ObjectiveDto] = payload.value;
                }
            } else {
                const objectiveConfigCriteriaToChange = objectiveConfig?.objectiveConfigCriterias.find((x) => x.id === payload.id);

                if (objectiveConfigCriteriaToChange) {
                    objectiveConfigCriteriaToChange.isDirty = true;
                    objectiveConfigCriteriaToChange.objectiveConfig_Id = payload.objectiveConfigId;
                    objectiveConfigCriteriaToChange.objectiveConfigCriteriaType_Id = payload.currentCriteriaTypeId;
                    objectiveConfigCriteriaToChange[payload.fieldName as keyof ObjectiveDto] = payload.value;
                }
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getObjectives.pending, (state, action) => {
                state.gettingObjectives = true;
                state.status = 'loading';
            })
            .addCase(getObjectives.fulfilled, (state, action) => {
                state.gettingObjectives = false;
                state.objectives = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getObjectives.rejected, (state, action) => {
                state.gettingObjectives = false;
                state.objectives = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getObjective.pending, (state, action) => {
                state.gettingObjective = true;
                state.status = 'loading';
            })
            .addCase(getObjective.fulfilled, (state, action) => {
                state.gettingObjective = false;
                state.objective = action.payload;

                state.objective.objectiveConfigs.forEach((o) => {
                    if (!o.objectiveConfigCriterias.some((x) => x.id === 0)) {
                        o.objectiveConfigCriterias.push(blankObjectiveConfigCriteria);
                    }
                    if (!o.teamLeadObjectiveConfigCriterias.some((x) => x.id === 0)) {
                        o.teamLeadObjectiveConfigCriterias.push(blankTeamLeadObjectiveConfigCriteria);
                    }
                });

                state.status = 'succeeded';
            })
            .addCase(getObjective.rejected, (state, action) => {
                state.gettingObjective = false;
                state.objective = { ...blankObjectiveDto };
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveObjective.pending, (state, action) => {
                state.archivingObjective = true;
                state.status = 'loading';
            })
            .addCase(archiveObjective.fulfilled, (state, action) => {
                state.archivingObjective = false;
                state.objectives = state.objectives.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveObjective.rejected, (state, action) => {
                state.archivingObjective = false;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveConfig.pending, (state, action) => {
                state.archivingConfig = true;
            })
            .addCase(archiveConfig.fulfilled, (state, action) => {
                state.archivingConfig = false;
                state.objective.objectiveConfigs = state.objective.objectiveConfigs.filter((x) => x.id !== action.payload.id);
            })
            .addCase(archiveConfig.rejected, (state, action) => {
                state.archivingConfig = false;
                state.error = action.error.message;
            })
            .addCase(archiveConfigCriteria.pending, (state, action) => {
                state.archivingConfigCriteria = true;
            })
            .addCase(archiveConfigCriteria.fulfilled, (state, action) => {
                state.archivingConfigCriteria = false;
                let objectiveConfigToUpdate = state.objective.objectiveConfigs.find((x) => x.id === action.payload.objectiveConfig_Id);

                if (objectiveConfigToUpdate) {
                    if (action.payload.isTeamLeadConfig) {
                        objectiveConfigToUpdate.teamLeadObjectiveConfigCriterias =
                            objectiveConfigToUpdate?.teamLeadObjectiveConfigCriterias.filter((x) => x.id !== action.payload.id);
                    } else {
                        objectiveConfigToUpdate.objectiveConfigCriterias = objectiveConfigToUpdate?.objectiveConfigCriterias.filter(
                            (x) => x.id !== action.payload.id
                        );
                    }
                }
            })
            .addCase(saveObjective.fulfilled, (state, action) => {
                state.objective = action.payload;
            })
            .addCase(archiveConfigCriteria.rejected, (state, action) => {
                state.archivingConfigCriteria = false;
                state.error = action.error.message;
            })
            .addCase(saveConfig.pending, (state, action) => {
                state.savingConfigId = action.meta.arg.id ?? 0;
                state.savingConfig = true;
            })
            .addCase(saveConfig.fulfilled, (state, action) => {
                state.savingConfig = false;
                if (
                    state.objective.objectiveConfigs &&
                    state.savingConfigId !== 0 &&
                    state.objective.objectiveConfigs.find((c) => c.id === state.savingConfigId)
                ) {
                    state.objective.objectiveConfigs.splice(
                        state.objective.objectiveConfigs.findIndex((o: ObjectiveConfigDto) => o.id === state.savingConfigId),
                        1,
                        action.payload
                    );
                    state.savingConfigId = 0;
                } else {
                    state.objective.objectiveConfigs.push(action.payload);
                }

                let objectiveConfigToUpdate = state.objective.objectiveConfigs.find((x) => x.id === action.payload.id);

                if (objectiveConfigToUpdate) {
                    objectiveConfigToUpdate.objectiveConfigCriterias.push(blankObjectiveConfigCriteria);
                    objectiveConfigToUpdate.teamLeadObjectiveConfigCriterias.push(blankTeamLeadObjectiveConfigCriteria);
                }
            })
            .addCase(saveConfig.rejected, (state, action) => {
                state.savingConfig = false;
                state.error = action.error.message;
            })
            .addCase(saveConfigCriteria.pending, (state, action) => {
                state.savingConfigCriteria = true;
            })
            .addCase(saveConfigCriteria.fulfilled, (state, action) => {
                state.savingConfigCriteria = false;

                let objectiveConfigToUpdate = state.objective.objectiveConfigs.find((x) => x.id === action.payload.objectiveConfig_Id);

                if (objectiveConfigToUpdate) {
                    if (action.payload.isTeamLeadConfig) {
                        let criterias = objectiveConfigToUpdate?.teamLeadObjectiveConfigCriterias;

                        if (criterias && criterias.find((c) => c.id === action.payload.id)) {
                            criterias.splice(
                                criterias.findIndex((o: ObjectiveConfigCriteriaDto) => o.id === action.payload.id),
                                1,
                                action.payload
                            );
                        } else {
                            objectiveConfigToUpdate.teamLeadObjectiveConfigCriterias =
                                objectiveConfigToUpdate?.teamLeadObjectiveConfigCriterias || [];
                            objectiveConfigToUpdate.teamLeadObjectiveConfigCriterias =
                                objectiveConfigToUpdate.teamLeadObjectiveConfigCriterias.filter((x) => x.id !== 0);
                            objectiveConfigToUpdate.teamLeadObjectiveConfigCriterias.push(action.payload);
                            objectiveConfigToUpdate.teamLeadObjectiveConfigCriterias.push(blankTeamLeadObjectiveConfigCriteria);
                        }
                    } else {
                        let criterias = objectiveConfigToUpdate?.objectiveConfigCriterias;

                        if (criterias && criterias.find((c) => c.id === action.payload.id)) {
                            criterias.splice(
                                criterias.findIndex((o: ObjectiveConfigCriteriaDto) => o.id === action.payload.id),
                                1,
                                action.payload
                            );
                        } else {
                            objectiveConfigToUpdate.objectiveConfigCriterias = objectiveConfigToUpdate?.objectiveConfigCriterias || [];
                            objectiveConfigToUpdate.objectiveConfigCriterias = objectiveConfigToUpdate.objectiveConfigCriterias.filter(
                                (x) => x.id !== 0
                            );
                            objectiveConfigToUpdate.objectiveConfigCriterias.push(action.payload);
                            objectiveConfigToUpdate.objectiveConfigCriterias.push(blankObjectiveConfigCriteria);
                        }
                    }
                }
            })
            .addCase(saveConfigCriteria.rejected, (state, action) => {
                state.savingConfigCriteria = false;
                state.error = action.error.message;
            });
    },
});

export const { clearObjectives } = objectiveSlice.actions;
export const { updateObjective } = objectiveSlice.actions;
export const { updateObjectiveConfig } = objectiveSlice.actions;
export const { updateObjectiveConfigCriteria } = objectiveSlice.actions;

export const getObjectives = createAsyncThunk('objectives/getObjectives', async (referenceYearId: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/objectives/getObjectives/${referenceYearId}`);

    return response.data;
});

export const getObjective = createAsyncThunk('objectives/getObjective', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/objectives/${id}`);

    return response.data;
});

export const archiveObjective = createAsyncThunk('objectives/archiveObjective', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/objectives/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedObjective'),
        })
    );
    return response.data;
});

export const archiveConfig = createAsyncThunk('objectives/archiveConfig', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/objective-configs/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedConfig'),
        })
    );
    return response.data;
});

export const archiveConfigCriteria = createAsyncThunk('objectives/archiveConfigCriteria', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/objective-configs/criterias/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedConfigCriteria'),
        })
    );
    return response.data;
});
export const saveObjective = createAsyncThunk('objectives/saveObjective', async (objectiveToSave: ObjectiveDto, thunkAPI) => {
    if (objectiveToSave.id === 0) {
        const response = await axios.post(`/api/synergy/objectives/create`, objectiveToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('createObjective'),
            })
        );
        return response.data;
    } else {
        const response = await axios.put(`/api/synergy/objectives/put`, objectiveToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('saveObjective'),
            })
        );
        return response.data;
    }
});

export const saveConfig = createAsyncThunk('configs/saveConfig', async (configToSave: ObjectiveConfigDto, thunkAPI) => {
    if (configToSave.id === 0) {
        const response = await axios.post(`/api/synergy/objective-configs/create`, configToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('createConfig'),
            })
        );
        return response.data;
    } else {
        const response = await axios.put(`/api/synergy/objective-configs/put`, configToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('saveConfig'),
            })
        );
        return response.data;
    }
});

export const saveConfigCriteria = createAsyncThunk(
    'configs/saveConfigCriteria',
    async (configCriteriaToSave: ObjectiveConfigCriteriaDto, thunkAPI) => {
        if (configCriteriaToSave.id === 0) {
            const response = await axios.post(`/api/synergy/objective-configs/criterias/create`, configCriteriaToSave);
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('createConfigCriteria'),
                })
            );
            return response.data;
        } else {
            const response = await axios.put(`/api/synergy/objective-configs/criterias/put`, configCriteriaToSave);
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('saveConfigCriteria'),
                })
            );
            return response.data;
        }
    }
);
